// Shared constants & helpers for Nilai v2
export const MAPEL_AKADEMIK = ["Matematika", "Pendidikan Pancasila", "PAI", "Bahasa Inggris", "IPAS", "Bahasa Indonesia", "Calistung", "PJOK", "IPA", "IPS"];
export const MAPEL_NON = ["Seni Musik", "Seni Tari", "Seni Lukis", "Tata Boga", "TIK", "Desain Grafis", "Prakarya", "Pengembangan Diri", "OMSK", "Komunikasi", "Pramuka", "Boccee", "Athletik"];
export const MAPEL_EKSTRA = ["Bahasa Reseptif", "Bahasa Ekspresif", "Preakademik", "Kemampuan Imitasi", "Bina Diri", "Kemampuan Wicara", "Calistung"];
export const TAHUN_AJARAN = ["2023/2024", "2024/2025", "2025/2026", "2026/2027"];

export const BULAN_SEMI = [
  { v: "07", label: "Juli" }, { v: "08", label: "Agustus" },
  { v: "10", label: "Oktober" }, { v: "11", label: "November" },
];
export const BULAN_SEM2 = [
  { v: "01", label: "Januari" }, { v: "02", label: "Februari" },
  { v: "04", label: "April" }, { v: "05", label: "Mei" },
];

export const JENIS_NILAI_AKADEMIK = [
  { v: "tugas_pr", label: "Tugas & PR (1-10)" },
  { v: "uh", label: "Ulangan Harian (1-6)" },
  { v: "uts", label: "UTS" },
  { v: "uas", label: "UAS" },
  { v: "nr", label: "Nilai Rapor (manual)" },
];
export const JENIS_NILAI_NON = [
  { v: "uh", label: "Ulangan Harian (1-6)" },
  { v: "pts", label: "PTS" },
  { v: "pas", label: "PAS" },
];

export function scoreToLetter(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 80) return "A";
  if (n >= 70) return "B";
  if (n >= 60) return "C";
  if (n >= 50) return "D";
  return "E";
}

export function letterColor(L) {
  return ({ A: "bg-emerald-100 text-emerald-700", B: "bg-sky-100 text-sky-700", C: "bg-amber-100 text-amber-700", D: "bg-orange-100 text-orange-700", E: "bg-rose-100 text-rose-700" })[L] || "bg-slate-100 text-slate-600";
}

export function avg(arr) {
  const valid = arr.filter((x) => x != null && !isNaN(x));
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

// Group entries into a hierarchical map: { siswa_id-ta-sem-mapel: { meta, entries[] } }
export function groupNilai(entries) {
  const groups = {};
  for (const e of entries) {
    const k = `${e.siswa_id}|${e.tahun_ajaran}|${e.semester}|${e.mata_pelajaran}|${e.jenis}`;
    if (!groups[k]) {
      groups[k] = {
        key: k, siswa_id: e.siswa_id, nama_siswa: e.nama_siswa, jenis: e.jenis,
        tahun_ajaran: e.tahun_ajaran, semester: e.semester, mata_pelajaran: e.mata_pelajaran,
        entries: [],
      };
    }
    groups[k].entries.push(e);
  }
  return Object.values(groups);
}

// Compute aggregates for a group based on jenis
export function computeAggregate(g) {
  if (g.jenis === "akademik") {
    const tugas = g.entries.filter((e) => e.jenis_nilai === "tugas_pr").map((e) => e.skor);
    const uh = g.entries.filter((e) => e.jenis_nilai === "uh").map((e) => e.skor);
    const uts = g.entries.find((e) => e.jenis_nilai === "uts")?.skor ?? null;
    const uas = g.entries.find((e) => e.jenis_nilai === "uas")?.skor ?? null;
    const nrManual = g.entries.find((e) => e.jenis_nilai === "nr")?.skor ?? null;
    const RUT = avg(tugas);
    const RUH = avg(uh);
    let NR = nrManual;
    if (NR == null && RUT != null && uts != null && uas != null) {
      NR = RUT * 0.6 + uts * 0.2 + uas * 0.2;
    }
    return { RUT, RUH, UTS: uts, UAS: uas, NR, letter: NR != null ? scoreToLetter(NR) : "—" };
  }
  if (g.jenis === "non_akademik") {
    const uh = g.entries.filter((e) => e.jenis_nilai === "uh").map((e) => e.skor);
    const pts = g.entries.find((e) => e.jenis_nilai === "pts")?.skor ?? null;
    const pas = g.entries.find((e) => e.jenis_nilai === "pas")?.skor ?? null;
    const RUH = avg(uh);
    let NR = null;
    if (RUH != null && pts != null && pas != null) NR = RUH * 0.6 + pts * 0.2 + pas * 0.2;
    return { RUH, PTS: pts, PAS: pas, NR, letter: NR != null ? scoreToLetter(NR) : "—" };
  }
  if (g.jenis === "ekstra") {
    // Group by (materi, aktifitas)
    const byActivity = {};
    g.entries.forEach((e) => {
      const k = `${e.materi || ""}|${e.aktifitas || ""}`;
      if (!byActivity[k]) byActivity[k] = { materi: e.materi, aktifitas: e.aktifitas, bulan: {}, pts: null, pas: null };
      if (e.jenis_nilai === "pts") byActivity[k].pts = e.skor;
      else if (e.jenis_nilai === "pas") byActivity[k].pas = e.skor;
      else if (e.bulan) {
        if (!byActivity[k].bulan[e.bulan]) byActivity[k].bulan[e.bulan] = [];
        byActivity[k].bulan[e.bulan][e.nilai_ke - 1] = e.skor;
      }
    });
    return { activities: Object.values(byActivity).map((a) => {
      const RTbulanan = Object.fromEntries(Object.entries(a.bulan).map(([b, vs]) => [b, avg(vs)]));
      const RT = avg(Object.values(RTbulanan));
      let NR = null;
      if (RT != null && a.pts != null && a.pas != null) NR = RT * 0.6 + a.pts * 0.2 + a.pas * 0.2;
      return { ...a, RTbulanan, RT, NR, letter: NR != null ? scoreToLetter(NR) : "—" };
    })};
  }
  return {};
}
